import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { Select } from 'src/design/atoms/Select';
import { Stack } from 'src/design/atoms/Stack';
import { Switch } from 'src/design/atoms/Switch';
import { TextInput } from 'src/design/atoms/TextInput';
import { Header } from 'src/design/organisms/Header';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { useApiConnections } from 'src/api/queries/organization/useApiConnections';
import {
  useProactSettings,
  useUpdateProactSettings,
} from 'src/api/queries/admin/useProactSettings';
import { getErrorMessage } from 'src/utils';

const validationSchema = Yup.object().shape({
  isEnabled: Yup.boolean(),
  apiConnectionId: Yup.string().when('isEnabled', {
    is: true,
    then: (schema) =>
      schema.required('API connection is required when Proact is enabled'),
  }),
  inspectionsPath: Yup.string()
    .required('Inspections path is required')
    .max(200)
    .matches(/^\//, 'Must start with /'),
});

export const Proact: React.FC = () => {
  const { data: connections = [] } = useApiConnections();
  const { data: settings, error: loadError } = useProactSettings();
  const updateSettings = useUpdateProactSettings();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      isEnabled: settings?.isEnabled ?? false,
      apiConnectionId: settings?.apiConnectionId ?? '',
      inspectionsPath: settings?.inspectionsPath ?? '/integration/v1',
    },
    validationSchema,
    onSubmit: (values) =>
      updateSettings
        .mutateAsync({
          isEnabled: values.isEnabled,
          apiConnectionId: values.apiConnectionId || undefined,
          inspectionsPath: values.inspectionsPath,
        })
        .then(() => undefined)
        .catch(() => undefined),
  });

  const selectedConnection = connections.find(
    (c) => c.id === formik.values.apiConnectionId,
  );

  return (
    <>
      <Header
        breadcrumbProps={{ items: [{ text: 'Settings' }, { text: 'Proact' }] }}
        title="Proact"
        helpText="Connect Firetrol to Proact. Users see their Proact inspections on the Inspections page, matched by email address."
      />

      {loadError && (
        <Alert color="red" title="Error" mb="md">
          {getErrorMessage(loadError)}
        </Alert>
      )}

      <Paper p="md" withBorder>
        <Stack>
          <Switch
            label="Enabled"
            description="When off, the Inspections page reports that the user is not linked."
            checked={formik.values.isEnabled}
            onChange={(e) =>
              formik.setFieldValue('isEnabled', e.currentTarget.checked)
            }
          />

          <Select
            label="API Connection"
            description="The Proact host and API key."
            placeholder="Select a connection..."
            required={formik.values.isEnabled}
            clearable
            data={connections.map((c) => ({
              value: c.id,
              label: c.isEnabled ? c.name : `${c.name} (disabled)`,
            }))}
            value={formik.values.apiConnectionId || null}
            onChange={(val) =>
              formik.setFieldValue('apiConnectionId', val ?? '')
            }
            onBlur={() => formik.setFieldTouched('apiConnectionId')}
            error={
              formik.touched.apiConnectionId && formik.errors.apiConnectionId
            }
          />

          {selectedConnection && !selectedConnection.isEnabled && (
            <Alert color="yellow" title="Connection disabled">
              The selected API connection is disabled, so Proact calls will not
              be made until it is enabled.
            </Alert>
          )}

          <TextInput
            label="Inspections API Path"
            description="Appended to the connection host to reach the inspections endpoint."
            placeholder="/integration/v1"
            required
            value={formik.values.inspectionsPath}
            onChange={(e) =>
              formik.setFieldValue('inspectionsPath', e.target.value)
            }
            onBlur={() => formik.setFieldTouched('inspectionsPath')}
            error={
              formik.touched.inspectionsPath && formik.errors.inspectionsPath
            }
          />

          <Group justify="flex-end">
            <SaveButton
              onClick={() => formik.handleSubmit()}
              loading={formik.isSubmitting}
              disabled={!settings || !formik.isValid || !formik.dirty}
            />
          </Group>
        </Stack>
      </Paper>
    </>
  );
};
